import React, { useState } from 'react';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { Pencil, Trash2, MoreHorizontal } from 'lucide-react';
import { entriesApi } from '../../../../api/entries';
import { projectsApi } from '../../../../api/projects';
import { TimeEntry, Project, TimeEntryStatus } from '../../../../types';
import { formatDate, getDayName, weekToDateRange, cn } from '../../../../lib/utils';
import { MIN_DAILY_HOURS } from '../../../../lib/timeEntryHelpers';
import { Button } from '../../../../components/ui/atoms/button';
import { StatusPill } from '../../../../components/ui/atoms/status-pill';
import { Input } from '../../../../components/ui/atoms/input';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../../components/ui/molecules/table';
import { CollapsibleBandRow, ExpandCollapseAll } from '../../../../components/ui/molecules/collapsible-band';
import { useCollapsibleGroups } from '../../../../lib/useCollapsibleGroups';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../../components/ui/molecules/select';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from '../../../../components/ui/molecules/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../../components/ui/organisms/dialog';
import { STATUS_CONFIG, FROZEN_STATUS_CONFIG, CLOSED_STATUS_CONFIG, WIDEST_STATUS_LABEL } from '../../../../components/ui/organisms/calendar-view';
import {
  getPhaseName, getEntryProjectInactiveStatus,
  groupEntriesByWeek, sortWeekKeys, weekKeyRange, totalsByDay, sumEntryHours,
} from '../utils';

export function ListView({
  onEdit,
  onFinalizeDay,
}: {
  onEdit: (entry: TimeEntry) => void;
  onFinalizeDay: (date: string, removeId: string) => void;
}) {
  const queryClient = useQueryClient();

  const [page, setPage]             = useState(1);
  const [from, setFrom]             = useState('');
  const [to, setTo]                 = useState('');
  const [weekFilter, setWeekFilter] = useState('');
  const [status, setStatus]         = useState('');

  // Entry awaiting delete confirmation; `checking` covers the day lookup that
  // decides between a straight delete and the finalize dialog.
  const [pendingDelete, setPendingDelete] = useState<TimeEntry | null>(null);
  const [checking, setChecking]           = useState(false);

  const handleWeekFilter = (val: string) => {
    setWeekFilter(val);
    const week = parseInt(val, 10);
    if (!val || isNaN(week) || week < 1 || week > 53) return;
    const { from: f, to: t } = weekToDateRange(new Date().getFullYear(), week);
    setFrom(f);
    setTo(t);
    setPage(1);
  };

  const { data, isLoading } = useQuery({
    queryKey: ['my-entries', page, from, to, status],
    queryFn: () =>
      entriesApi.listMine({ page, limit: 20, from: from || undefined, to: to || undefined, status: status || undefined })
        .then((r) => r.data),
  });

  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsApi.list().then((r) => r.data),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => entriesApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['my-entries'] });
      queryClient.invalidateQueries({ queryKey: ['my-calendar'] });
      queryClient.invalidateQueries({ queryKey: ['my-entries-for-day'] });    },
  });

  /**
   * A delete that leaves the day short of the daily minimum is handed to the
   * finalize dialog instead of going straight out, so the entry is removed in
   * the same transaction as whatever replaces its hours. Clearing the day
   * entirely is left alone — a day with nothing logged is a valid state, and
   * demanding 8h there would trap the employee.
   *
   * The day is fetched rather than read off the page: the list is paginated
   * and filtered, so a day's other entries may not all be on screen.
   */
  const confirmDelete = async () => {
    const entry = pendingDelete;
    if (!entry) return;
    setChecking(true);

    const date = entry.date.slice(0, 10);
    const dayEntries = await queryClient.fetchQuery({
      queryKey: ['my-entries-for-day', date],
      queryFn: () =>
        entriesApi.listMine({ from: date, to: date, limit: 50 })
          .then((r) => (r.data?.data ?? []).filter((e) => e.status !== 'REJECTED')),
    });

    const remaining = dayEntries
      .filter((e) => e.id !== entry.id)
      .reduce((sum, e) => sum + e.hours, 0);

    setChecking(false);
    setPendingDelete(null);

    if (remaining > 0 && remaining < MIN_DAILY_HOURS) {
      onFinalizeDay(date, entry.id);
      return;
    }
    deleteMutation.mutate(entry.id);
  };

  const projects: Project[]  = projectsData ?? [];
  const entries: TimeEntry[] = data?.data ?? [];
  const meta                 = data?.meta;

  const grouped  = groupEntriesByWeek(entries);
  const weekKeys = sortWeekKeys(grouped);
  const groups = useCollapsibleGroups(weekKeys);

  // Totals must cover the whole week, not just the slice that landed on this
  // page, so each visible week's entries are fetched in full (ignoring the
  // status filter and pagination). REJECTED entries don't count.
  const weekRanges = weekKeys.map((key) => ({ key, ...weekKeyRange(key) }));
  const weekQueries = useQueries({
    queries: weekRanges.map(({ from: wFrom, to: wTo }) => ({
      // Under the 'my-entries' prefix so existing invalidations refresh it too.
      queryKey: ['my-entries', 'week', wFrom, wTo],
      queryFn: () =>
        entriesApi.listMine({ from: wFrom, to: wTo, limit: 100 }).then((r) => r.data?.data ?? []),
    })),
  });
  // week key -> date -> total hours; absent until the week's query resolves (the
  // render hides totals meanwhile, and falls back to page entries if it fails).
  const fullWeekTotals: Record<string, Record<string, number>> = {};
  weekRanges.forEach(({ key }, i) => {
    const weekEntries = weekQueries[i]?.data;
    if (!weekEntries) return;
    fullWeekTotals[key] = totalsByDay(weekEntries);
  });

  return (
    <>
      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Week #</label>
          <Input type="number" min={1} max={53} placeholder="e.g. 25" value={weekFilter} onChange={(e) => handleWeekFilter(e.target.value)} className="w-24" />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">From</label>
          <Input type="date" value={from} onChange={(e) => { setFrom(e.target.value); setWeekFilter(''); setPage(1); }} className="w-36" />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">To</label>
          <Input type="date" value={to} onChange={(e) => { setTo(e.target.value); setWeekFilter(''); setPage(1); }} className="w-36" />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Status</label>
          <Select value={status} onValueChange={(v) => { setStatus(v === '__all__' ? '' : v); setPage(1); }}>
            <SelectTrigger className="w-44">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All statuses</SelectItem>
              {(Object.keys(STATUS_CONFIG) as TimeEntryStatus[]).map((s) => (
                <SelectItem key={s} value={s}>{STATUS_CONFIG[s].label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {(from || to || weekFilter || status) && (
          <div className="flex items-end">
            <Button variant="ghost" size="sm" onClick={() => { setFrom(''); setTo(''); setWeekFilter(''); setStatus(''); setPage(1); }}>Clear</Button>
          </div>
        )}
        <div className="flex items-end ml-auto">
          <ExpandCollapseAll
            allExpanded={groups.allExpanded}
            onExpandAll={groups.expandAll}
            onCollapseAll={groups.collapseAll}
            disabled={weekKeys.length === 0}
          />
        </div>
      </div>

      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Project</TableHead>
                <TableHead>Phase</TableHead>
                <TableHead>Job Title</TableHead>
                <TableHead>Hours</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {entries.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground">No entries found.</TableCell>
                </TableRow>
              )}
              {weekKeys.map((weekKey) => {
                const week     = Number(weekKey.slice(5));
                const dayMap   = grouped[weekKey];
                const dates    = Object.keys(dayMap).sort((a, b) => b.localeCompare(a));
                const fullDays  = fullWeekTotals[weekKey];
                // Hide totals until the full week arrives: page-only sums would
                // flash a false "under 8h" on a week split across pages. If the
                // request fails, fall back to the page's own entries.
                const weekQuery = weekQueries[weekKeys.indexOf(weekKey)];
                const totalsPending = !fullDays && !weekQuery?.isError;
                const weekTotal = fullDays
                  ? Object.values(fullDays).reduce((s, h) => s + h, 0)
                  : dates.reduce((s, d) => s + sumEntryHours(dayMap[d]), 0);
                return (
                  <React.Fragment key={weekKey}>
                    <CollapsibleBandRow
                      colSpan={7}
                      expanded={groups.isExpanded(weekKey)}
                      onToggle={() => groups.toggle(weekKey)}
                    >
                      <span className="font-semibold text-sm">Week {week}</span>
                      {!totalsPending && <span className="ml-3 text-xs opacity-70">{weekTotal}h total</span>}
                    </CollapsibleBandRow>
                    {groups.isExpanded(weekKey) && dates.map((dateStr) => {
                      const dayEntries = dayMap[dateStr];
                      const dayTotal   = fullDays?.[dateStr] ?? sumEntryHours(dayEntries);
                      return (
                        <React.Fragment key={dateStr}>
                          <TableRow className="bg-muted/25 hover:bg-muted/25">
                            <TableCell colSpan={7} className="py-1 pl-6">
                              <span className="text-sm font-medium">{getDayName(dateStr)}</span>
                              <span className="ml-2 text-xs text-muted-foreground">{formatDate(dateStr + 'T00:00:00')}</span>
                              {!totalsPending && (
                                <span className={cn('ml-3 text-xs', dayTotal < 8 ? 'text-amber-600 font-medium' : 'text-muted-foreground')}>
                                  {dayTotal}h{dayTotal < 8 ? ' (under 8h)' : ''}
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                          {dayEntries.map((entry) => {
                            const inactiveStatus = getEntryProjectInactiveStatus(entry, projects);
                            const isInactive = inactiveStatus !== null;
                            const entryStatus = inactiveStatus === 'FROZEN'
                              ? FROZEN_STATUS_CONFIG
                              : inactiveStatus === 'CLOSED'
                              ? CLOSED_STATUS_CONFIG
                              : STATUS_CONFIG[entry.status];
                            const canEdit = !isInactive && !!entry.ownerEditable;
                            return (
                            <TableRow key={entry.id} className={cn(isInactive && 'opacity-60 bg-muted/20')}>
                              <TableCell>{entry.project?.name ?? projects.find((p) => p.id === entry.projectId)?.name ?? entry.projectId}</TableCell>
                              <TableCell className="text-muted-foreground">{getPhaseName(projects, entry)}</TableCell>
                              <TableCell className="text-muted-foreground">{entry.jobTitle?.name ?? '—'}</TableCell>
                              <TableCell>{entry.hours}h</TableCell>
                              <TableCell>
                                <StatusPill {...entryStatus} sizeTo={WIDEST_STATUS_LABEL} />
                              </TableCell>
                              <TableCell className="max-w-xs truncate text-muted-foreground">{entry.notes ?? '—'}</TableCell>
                              <TableCell className="text-right">
                                {canEdit ? (
                                  <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                      <Button variant="ghost" size="icon">
                                        <MoreHorizontal className="h-4 w-4" />
                                      </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end">
                                      <DropdownMenuItem onClick={() => onEdit(entry)}>
                                        <Pencil className="h-4 w-4" />
                                        Edit
                                      </DropdownMenuItem>
                                      <DropdownMenuSeparator />
                                      <DropdownMenuItem
                                        className="text-destructive focus:text-destructive"
                                        onClick={() => setPendingDelete(entry)}
                                      >
                                        <Trash2 className="h-4 w-4" />
                                        Delete
                                      </DropdownMenuItem>
                                    </DropdownMenuContent>
                                  </DropdownMenu>
                                ) : null}
                              </TableCell>
                            </TableRow>
                            );
                          })}
                        </React.Fragment>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>

          {meta && meta.totalPages > 1 && (
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
              <span className="text-sm text-muted-foreground">Page {meta.page} of {meta.totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= meta.totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          )}
        </>
      )}

      {/* ── Delete confirmation ── */}
      <Dialog open={!!pendingDelete} onOpenChange={(open: boolean) => { if (!open && !checking) setPendingDelete(null); }}>
        {pendingDelete && (
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete this entry?</DialogTitle>
            </DialogHeader>

            <div className="rounded-md border px-3 py-2 text-sm">
              <p className="font-medium">
                {pendingDelete.project?.name ?? projects.find((p) => p.id === pendingDelete.projectId)?.name ?? 'Unknown project'}
              </p>
              <p className="text-muted-foreground">
                {formatDate(pendingDelete.date)} · {pendingDelete.hours}h
              </p>
            </div>

            <p className="text-sm text-muted-foreground">
              This can't be undone. If the day drops below {MIN_DAILY_HOURS}h you'll be asked to
              fill the gap before anything is removed.
            </p>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setPendingDelete(null)} disabled={checking}>Cancel</Button>
              <Button variant="destructive" onClick={confirmDelete} disabled={checking}>
                {checking ? 'Checking…' : 'Delete'}
              </Button>
            </div>
          </DialogContent>
        )}
      </Dialog>
    </>
  );
}
